import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Briefcase, Copy, ExternalLink, Building2, MapPin, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { shareViaWhatsApp, WhatsAppIcon } from '../../lib/whatsapp';
import SellVirtualOfficeModal from './SellVirtualOfficeModal';
import WhatsAppPitchModal from './WhatsAppPitchModal';

const VirtualOfficeShareTab = ({ api, user, theme = {} }) => {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sellOpen, setSellOpen] = useState(false);
  const [defaultVenueId, setDefaultVenueId] = useState(null);
  const [pitchVenue, setPitchVenue] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/public/virtual-office/venues');
        setVenues(res.data || []);
      } catch (e) {
        toast.error('Failed to load venues');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const previewUrl = (venueId) => {
    const base = window.location.origin;
    return `${base}/preview/virtual-office/${venueId}${user?.id ? `?ref=${user.id}` : ''}`;
  };

  const copyLink = (venueId, venueName) => {
    const url = previewUrl(venueId);
    navigator.clipboard.writeText(url);
    toast.success(`Link copied · ${venueName}`);
  };

  const shareWA = (venueId, venueName) => {
    const url = previewUrl(venueId);
    const msg = `Hi! Check out Virtual Office plans at *${venueName}* — premium business address, GST support, mail handling and more:\n\n${url}`;
    shareViaWhatsApp(msg);
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="vo-share-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-primary" />
            Share Virtual Office Plans
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Share preview links with prospects or directly generate a payment link to close the sale.
          </p>
        </div>
        <Button onClick={() => { setDefaultVenueId(null); setSellOpen(true); }} data-testid="vo-share-sell-btn">
          <Briefcase className="w-4 h-4 mr-2" /> Sell to Client
        </Button>
      </div>

      {venues.length === 0 && (
        <Card className={theme.cardBg}>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-40" />
            <p>No centres have Virtual Office plans enabled yet.</p>
            <p className="text-xs mt-1">Ask an admin to enable plans in the Virtual Office tab.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {venues.map((v) => (
          <Card key={v.id} className={theme.cardBg} data-testid={`vo-share-card-${v.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{v.name}</h3>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3" /> {v.region || v.address || 'Bangalore'}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs">{v.active_vo_tiers} plans</Badge>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyLink(v.id, v.name)}
                  data-testid={`vo-copy-${v.id}`}
                  className="flex-1 min-w-[100px]"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy Link
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => shareWA(v.id, v.name)}
                  data-testid={`vo-whatsapp-${v.id}`}
                  className="flex-1 min-w-[100px] bg-green-50 hover:bg-green-100 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30"
                >
                  <WhatsAppIcon className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => window.open(previewUrl(v.id), '_blank')}
                  data-testid={`vo-preview-${v.id}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Button
                size="sm"
                onClick={() => { setDefaultVenueId(v.id); setSellOpen(true); }}
                className="w-full mt-2"
                data-testid={`vo-sell-${v.id}`}
              >
                <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Sell at this Centre
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPitchVenue(v)}
                className="w-full mt-2"
                data-testid={`vo-pitch-${v.id}`}
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> WhatsApp Pitch
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <SellVirtualOfficeModal open={sellOpen} onClose={() => setSellOpen(false)} api={api} defaultVenueId={defaultVenueId} />
      <WhatsAppPitchModal open={!!pitchVenue} onClose={() => setPitchVenue(null)} api={api} venue={pitchVenue} user={user} />
    </div>
  );
};

export default VirtualOfficeShareTab;
